import React, { useMemo } from 'react';
import Select, { components } from 'react-select';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import timezonesList from 'timezones-list';
import { isValidTimezone } from '../../utils/formatTimestamp';

export default function TimezoneSelect({ value, onChange, placeholder = 'Search timezone...' }) {
  // Timezones-list options for react-select (searchable dropdown with UTC offsets)
  // Filter out invalid timezone codes (timezones-list has some incorrect codes like Europe/Zaporizhzhia)
  const timezonesListOptions = useMemo(() => {
    return timezonesList
      .filter((tz) => isValidTimezone(tz.tzCode))
      .map((tz) => ({
        value: tz.tzCode,
        label: tz.label.replace('GMT', 'UTC').replace(/_/g, ' '),
        name: tz.name.replace('GMT', 'UTC').replace(/_/g, ' '),
        utc: tz.utc,
        tzCode: tz.tzCode,
      }));
  }, []);

  return (
    <Select
      classNamePrefix="timezone-select"
      options={timezonesListOptions}
      value={timezonesListOptions.find((opt) => opt.value === value) || null}
      onChange={(selected) => {
        onChange(selected ? selected.value : '');
      }}
      placeholder={placeholder}
      isClearable={false}
      isSearchable
      menuPlacement="auto"
      maxMenuHeight={200}
      components={{
        DropdownIndicator: (props) => (
          <components.DropdownIndicator {...props}>
            <ChevronDownIcon className="h-5 w-5 text-ink-muted" />
          </components.DropdownIndicator>
        ),
        IndicatorSeparator: () => null,
      }}
      styles={{
        control: (provided, state) => ({
          ...provided,
          minHeight: '28px',
          height: '28px',
          border: state.isFocused ? '1px solid #E0D9CC' : '1px solid #E0D9CC',
          backgroundColor: '#FFFFFF',
          boxShadow: 'none',
          borderRadius: '0.375rem',
          cursor: 'default',
          '&:hover': {
            border: '1px solid #E0D9CC',
          },
        }),
        valueContainer: (provided) => ({
          ...provided,
          height: '26px',
          padding: '0 8px',
        }),
        input: (provided) => ({
          ...provided,
          margin: '0',
          padding: '0',
          color: '#3A3429',
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          height: '26px',
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: '0 8px 0 0',
        }),
        placeholder: (provided) => ({
          ...provided,
          color: '#9A9285',
          fontSize: '14px',
        }),
        option: (provided, state) => ({
          ...provided,
          fontSize: '14px',
          padding: '8px 12px',
          backgroundColor: state.isSelected ? '#3A3429' : state.isFocused ? '#F0ECE4' : '#FCFBF8',
          color: state.isSelected ? 'white' : '#6B6458',
          cursor: 'default',
        }),
        singleValue: (provided) => ({
          ...provided,
          fontSize: '14px',
          color: '#3A3429',
        }),
        menu: (provided) => ({
          ...provided,
          marginTop: '4px',
          border: '1px solid #E0D9CC',
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          zIndex: 10,
        }),
        menuList: (provided) => ({
          ...provided,
          padding: '4px 0',
        }),
      }}
      filterOption={(option, inputValue) => {
        if (!inputValue) return true;
        const search = inputValue.toLowerCase();
        return (
          option.data.label?.toLowerCase().includes(search) ||
          option.data.name?.toLowerCase().includes(search) ||
          option.data.utc?.toLowerCase().includes(search) ||
          option.data.tzCode?.toLowerCase().includes(search)
        );
      }}
    />
  );
}
